import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash, Plus, ArrowLeft, User, Lock, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import {
  getStudentById,
  updateStudent,
} from "../../../services/studentServices";
import { changePassword } from "../../../services/studentServices";
import { deleteImage, uploadImages } from "../../../services/uploaderServices";
import Input from "../../../components/admin/common/Input";
import Sidebar from "../../../components/admin/common/Sidebar";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Header from "../../../components/admin/common/Header";

const EditStudent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState("basic");

  const [institutions, setInstitutions] = useState([
    {
      id: crypto.randomUUID(),
      name: "",
      location: "",
      degree: "",
      fieldOfStudy: "",
      yearStart: "",
      targetedYearCompletion: "",
    },
  ]);

  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [address, setAddress] = useState("");
  const [photo, setPhoto] = useState(null);
  const [bonafideCertificate, setBonafideCertificate] = useState(null);
  const [coursesCompleted, setCoursesCompleted] = useState("");

  const [photoUrl, setPhotoUrl] = useState(null);
  const [bonafideUrl, setBonafideUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBonafide, setUploadingBonafide] = useState(false);

  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  // Password change states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    const fetchStudentData = async () => {
      if (!id) {
        toast.error("Student ID is required");
        navigate("/admin/students");
        return;
      }

      try {
        setFetchingData(true);
        const response = await getStudentById(id);

        if (response.data) {
          const student = response.data;

          setFullName(student.fullName);
          setDateOfBirth(student.dateOfBirth.split("T")[0]);
          setEmail(student.email);
          setMobile(student.mobile);
          setAddress(student.address);
          setPhotoUrl(student.photo);
          setBonafideUrl(student.bonafideCertificate);
          setCoursesCompleted(student.hasCompletedSixCourses ? "Yes" : "No");
          const mappedInstitutions = student.institutionDetails.map((inst) => ({
            id: crypto.randomUUID(),
            name: inst.institutionName,
            yearStart: inst.yearOfStart.toString(),
            targetedYearCompletion: inst.targetedYearOfCompletion.toString(),
            ...inst,
          }));
          setInstitutions(
            mappedInstitutions.length > 0
              ? mappedInstitutions
              : [
                  {
                    id: crypto.randomUUID(),
                    name: "",
                    location: "",
                    degree: "",
                    fieldOfStudy: "",
                    yearStart: "",
                    targetedYearCompletion: "",
                  },
                ]
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch student data";
        toast.error(errorMessage);
      } finally {
        setFetchingData(false);
      }
    };

    fetchStudentData();
  }, [id, navigate]);

  const handleMobileChange = (value) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue.length <= 10) {
      setMobile(cleanedValue);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append("images", file);

      const response = await uploadImages(formData);

      if (response.success && response.images?.[0]?.imageUrl) {
        setPhotoUrl(response.images[0].imageUrl);
        setPhoto(file);
        toast.success("Photo uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to upload photo";
      toast.error(errorMessage);
      setPhoto(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBonafideUpload = async (file) => {
    if (!file) return;

    setUploadingBonafide(true);

    try {
      const formData = new FormData();
      formData.append("images", file);

      const response = await uploadImages(formData);

      if (response.success && response.images?.[0]?.imageUrl) {
        setBonafideUrl(response.images[0].imageUrl);
        setBonafideCertificate(file);
        toast.success("Bonafide certificate uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to upload bonafide certificate";
      toast.error(errorMessage);
      setBonafideCertificate(null);
    } finally {
      setUploadingBonafide(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoUrl) return;

    try {
      await deleteImage(photoUrl);
      setPhotoUrl(null);
      setPhoto(null);
      toast.success("Photo removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete photo";
      toast.error(errorMessage);
    }
  };

  const handleDeleteBonafide = async () => {
    if (!bonafideUrl) return;

    try {
      await deleteImage(bonafideUrl);
      setBonafideUrl(null);
      setBonafideCertificate(null);
      toast.success("Bonafide certificate removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete bonafide certificate";
      toast.error(errorMessage);
    }
  };

  const handleAddInstitution = () => {
    setInstitutions((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: "",
        location: "",
        degree: "",
        fieldOfStudy: "",
        yearStart: "",
        targetedYearCompletion: "",
      },
    ]);
  };

  const handleDeleteInstitution = (id) => {
    setInstitutions((prev) => prev.filter((inst) => inst.id !== id));
  };

  const handleInstitutionChange = (id, field, value) => {
    setInstitutions((prev) =>
      prev.map((inst) =>
        inst.id === id
          ? {
              ...inst,
              [field]: value,
            }
          : inst
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!id) {
      toast.error("Student ID is missing");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fullName,
        dateOfBirth,
        email,
        mobile,
        address,
        institutionDetails: institutions.map(({ id, ...rest }) => rest),
        hasCompletedSixCourses: coursesCompleted === "Yes",
        photo: photoUrl,
        bonafideCertificate: bonafideUrl,
      };

      await updateStudent(id, payload);
      toast.success("Student information updated successfully!");
      navigate("/admin/members/students/list");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update student";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("All password fields are required");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    if (currentPassword === newPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(id, {
        currentPassword,
        newPassword,
        confirmPassword,
      });

      toast.success("Password changed successfully!");

      // Reset password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to change password";
      toast.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E2A46] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading student data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen p-4 gap-4 bg-white">
      <div className="flex-shrink-0">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <div className="p-6">
                <Header
                  title="Update Student"
                  desc="Review, approve, or reject student registered applications"
                />

                <div className="container mx-auto py-6">
                  <div className="max-w-7xl mx-auto">
                    <div className="mb-6">
                      <button
                        onClick={() => navigate("/admin/members/students/list")}
                        className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors font-medium"
                      >
                        <ArrowLeft size={20} />
                        <span>Back to Students</span>
                      </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
                      <div className="flex border-b border-gray-200">
                        <button
                          onClick={() => setActiveTab("basic")}
                          className={`flex items-center gap-2 px-6 py-4 rounded-l-xl text-sm font-semibold transition-all ${
                            activeTab === "basic"
                              ? "text-[#4F22D8] border-b-2 border-[#4F22D8] bg-purple-50"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                          }`}
                        >
                          <User size={18} />
                          Basic Information
                        </button>
                        <button
                          onClick={() => setActiveTab("password")}
                          className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-all ${
                            activeTab === "password"
                              ? "text-[#4F22D8] border-b-2 border-[#4F22D8] bg-purple-50"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                          }`}
                        >
                          <Lock size={18} />
                          Change Password
                        </button>
                      </div>
                    </div>

                    {/* Tab Content */}
                    {activeTab === "basic" && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <form onSubmit={handleSubmit}>
                          <div className="p-8 border-b border-gray-100">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6">
                              Personal Details
                            </h2>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Enter Full Name{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={fullName}
                                  onChange={(e) => setFullName(e.target.value)}
                                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                  placeholder="Enter full name"
                                />
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Date of Birth{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <input
                                  type="date"
                                  value={dateOfBirth}
                                  onChange={(e) =>
                                    setDateOfBirth(e.target.value)
                                  }
                                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Upload Photo{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  id="photoUpload"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handlePhotoUpload(file);
                                  }}
                                />
                                <label
                                  htmlFor="photoUpload"
                                  className={`flex items-center justify-center px-4 py-2.5 border-2 border-dashed rounded-lg text-sm font-medium transition-all cursor-pointer ${
                                    uploadingPhoto
                                      ? "cursor-not-allowed bg-gray-50 border-gray-300 text-gray-400"
                                      : "bg-white border-gray-300 text-gray-700 hover:border-purple-500 hover:text-purple-600"
                                  }`}
                                >
                                  {uploadingPhoto
                                    ? "Uploading..."
                                    : "Choose Photo"}
                                </label>
                                {photoUrl && (
                                  <div className="mt-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-600 truncate max-w-[200px]">
                                        {photo ? photo.name : "Current photo"}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={handleDeletePhoto}
                                        className="px-3 py-1 bg-red text-white text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                    <img
                                      src={photoUrl}
                                      alt="Uploaded photo preview"
                                      className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                    />
                                  </div>
                                )}
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Upload Bonafide Image{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  id="bonafideUpload"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleBonafideUpload(file);
                                  }}
                                />
                                <label
                                  htmlFor="bonafideUpload"
                                  className={`flex items-center justify-center px-4 py-2.5 border-2 border-dashed rounded-lg text-sm font-medium transition-all cursor-pointer ${
                                    uploadingBonafide
                                      ? "cursor-not-allowed bg-gray-50 border-gray-300 text-gray-400"
                                      : "bg-white border-gray-300 text-gray-700 hover:border-purple-500 hover:text-purple-600"
                                  }`}
                                >
                                  {uploadingBonafide
                                    ? "Uploading..."
                                    : "Choose Bonafide Image"}
                                </label>
                                {bonafideUrl && (
                                  <div className="mt-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-600 truncate max-w-[200px]">
                                        {bonafideCertificate
                                          ? bonafideCertificate.name
                                          : "Current certificate"}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={handleDeleteBonafide}
                                        className="px-3 py-1 bg-red text-white text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                    <img
                                      src={bonafideUrl}
                                      alt="Uploaded bonafide certificate preview"
                                      className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="p-8 border-b border-gray-100">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6">
                              Communication Details
                            </h2>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Email ID <span className="text-red">*</span>
                                </label>
                                <div className="relative">
                                  <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder="Enter email address"
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Mobile (10 digits){" "}
                                  <span className="text-red">*</span>
                                </label>
                                <div className="flex gap-2">
                                  <div className="flex items-center justify-center px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-sm font-medium text-gray-700">
                                    +91
                                  </div>
                                  <input
                                    type="tel"
                                    value={mobile}
                                    onChange={(e) =>
                                      handleMobileChange(e.target.value)
                                    }
                                    className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder="Enter 10-digit number"
                                    maxLength={10}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="mt-6 space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Address <span className="text-red">*</span>
                              </label>
                              <textarea
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                rows={4}
                                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                                placeholder="Enter complete address"
                              />
                            </div>
                          </div>

                          <div className="p-8 border-b border-gray-100">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6">
                              Institutional Details
                            </h2>

                            {institutions.map((inst, index) => (
                              <div
                                key={inst.id}
                                className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200"
                              >
                                <div className="flex justify-between items-start mb-4">
                                  <h3 className="text-sm font-semibold text-gray-700">
                                    Institution {index + 1}
                                  </h3>
                                  <div className="flex gap-2">
                                    {institutions.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDeleteInstitution(inst.id)
                                        }
                                        className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                        title="Delete Institution"
                                      >
                                        <Trash size={16} />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={handleAddInstitution}
                                      className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                      title="Add Institution"
                                    >
                                      <Plus size={16} />
                                    </button>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  <div className="lg:col-span-2 space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Name of Present Institution{" "}
                                      <span className="text-red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={inst.name}
                                      onChange={(e) =>
                                        handleInstitutionChange(
                                          inst.id,
                                          "name",
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                      placeholder="Enter institution name"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Location{" "}
                                      <span className="text-red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={inst.location}
                                      onChange={(e) =>
                                        handleInstitutionChange(
                                          inst.id,
                                          "location",
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                      placeholder="Enter location"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Degree <span className="text-red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={inst.degree}
                                      onChange={(e) =>
                                        handleInstitutionChange(
                                          inst.id,
                                          "degree",
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                      placeholder="Enter degree"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Field of Study{" "}
                                      <span className="text-red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={inst.fieldOfStudy}
                                      onChange={(e) =>
                                        handleInstitutionChange(
                                          inst.id,
                                          "fieldOfStudy",
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                      placeholder="Enter field of study"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Year of Start{" "}
                                      <span className="text-red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={inst.yearStart}
                                      onChange={(e) =>
                                        handleInstitutionChange(
                                          inst.id,
                                          "yearStart",
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                      placeholder="Enter year"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                      Targeted Year of Completion{" "}
                                      <span className="text-red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={inst.targetedYearCompletion}
                                      onChange={(e) =>
                                        handleInstitutionChange(
                                          inst.id,
                                          "targetedYearCompletion",
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                      placeholder="Enter year"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="p-8">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">
                              Past, Relevant Studies
                            </h2>
                            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                              List all degree programs in aquaculture pursued
                              prior to present program providing name of the
                              institution, degree, major, number of years of
                              study
                            </p>

                            <div className="space-y-4">
                              <p className="text-sm font-medium text-gray-700">
                                Have you completed at least six courses in
                                aquaculture or a related discipline?
                                <span className="text-red ml-1">*</span>
                              </p>

                              <div className="flex gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="coursesCompleted"
                                    value="Yes"
                                    checked={coursesCompleted === "Yes"}
                                    onChange={(e) =>
                                      setCoursesCompleted(e.target.value)
                                    }
                                    className="w-4 h-4 text-purple-600 focus:ring-2 focus:ring-purple-500"
                                  />
                                  <span className="text-sm font-medium text-gray-700">
                                    Yes
                                  </span>
                                </label>

                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="coursesCompleted"
                                    value="No"
                                    checked={coursesCompleted === "No"}
                                    onChange={(e) =>
                                      setCoursesCompleted(e.target.value)
                                    }
                                    className="w-4 h-4 text-purple-600 focus:ring-2 focus:ring-purple-500"
                                  />
                                  <span className="text-sm font-medium text-gray-700">
                                    No
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex gap-4">
                            <button
                              type="submit"
                              disabled={loading}
                              className="px-6 py-2.5 bg-[#4F22D8] text-white text-sm font-semibold rounded-lg transition-colors hover:bg-[#4119b8] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loading ? "Updating..." : "Update Student"}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                navigate("/admin/members/students/list")
                              }
                              disabled={loading}
                              className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* Change Password Tab Content */}
                    {activeTab === "password" && (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <form onSubmit={handlePasswordChange}>
                          <div className="p-8">
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                              Change Password
                            </h2>
                            <p className="text-sm text-gray-600 mb-6">
                              Update your password to keep your account secure
                            </p>

                            <div className="max-w-2xl space-y-6">
                              {/* Current Password */}
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Current Password{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <div className="relative">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                  </div>
                                  <input
                                    type={
                                      showCurrentPassword ? "text" : "password"
                                    }
                                    value={currentPassword}
                                    onChange={(e) =>
                                      setCurrentPassword(e.target.value)
                                    }
                                    className="w-full pl-14 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder="Enter current password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowCurrentPassword(
                                        !showCurrentPassword
                                      )
                                    }
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                  >
                                    {showCurrentPassword ? (
                                      <Eye
                                        size={18}
                                        className="text-gray-400 hover:text-gray-600"
                                      />
                                    ) : (
                                      <EyeOff
                                        size={18}
                                        className="text-gray-400 hover:text-gray-600"
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {/* New Password */}
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  New Password{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <div className="relative">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                  </div>
                                  <input
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) =>
                                      setNewPassword(e.target.value)
                                    }
                                    className="w-full pl-14 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder="Enter new password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowNewPassword(!showNewPassword)
                                    }
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                  >
                                    {showNewPassword ? (
                                      <Eye
                                        size={18}
                                        className="text-gray-400 hover:text-gray-600"
                                      />
                                    ) : (
                                      <EyeOff
                                        size={18}
                                        className="text-gray-400 hover:text-gray-600"
                                      />
                                    )}
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Password must be at least 8 characters long
                                </p>
                              </div>

                              {/* Confirm Password */}
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Confirm New Password{" "}
                                  <span className="text-red">*</span>
                                </label>
                                <div className="relative">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                  </div>
                                  <input
                                    type={
                                      showConfirmPassword ? "text" : "password"
                                    }
                                    value={confirmPassword}
                                    onChange={(e) =>
                                      setConfirmPassword(e.target.value)
                                    }
                                    className="w-full pl-14 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder="Confirm new password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        !showConfirmPassword
                                      )
                                    }
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                  >
                                    {showConfirmPassword ? (
                                      <Eye
                                        size={18}
                                        className="text-gray-400 hover:text-gray-600"
                                      />
                                    ) : (
                                      <EyeOff
                                        size={18}
                                        className="text-gray-400 hover:text-gray-600"
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {/* Password Requirements */}
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                                  Password Requirements:
                                </h4>
                                <ul className="text-xs text-blue-800 space-y-1">
                                  <li className="flex items-start gap-2">
                                    <span className="text-blue-600 mt-0.5">
                                      •
                                    </span>
                                    <span>At least 8 characters long</span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <span className="text-blue-600 mt-0.5">
                                      •
                                    </span>
                                    <span>
                                      New password must be different from
                                      current password
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-2">
                                    <span className="text-blue-600 mt-0.5">
                                      •
                                    </span>
                                    <span>
                                      New password and confirm password must
                                      match
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex gap-4">
                            <button
                              type="submit"
                              disabled={passwordLoading}
                              className="px-6 py-2.5 bg-[#4F22D8] text-white text-sm font-semibold rounded-lg transition-colors hover:bg-[#4119b8] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {passwordLoading
                                ? "Changing Password..."
                                : "Change Password"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setCurrentPassword("");
                                setNewPassword("");
                                setConfirmPassword("");
                                setShowCurrentPassword(false);
                                setShowNewPassword(false);
                                setShowConfirmPassword(false);
                              }}
                              disabled={passwordLoading}
                              className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Reset
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default EditStudent;
